import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import { clientService } from "../services/apiService.jsx";
import "./Client.css"; // or wherever your global styles are

const CreateClient = () => {
  const navigate = useNavigate();
  const formRef = useRef();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");

  const [formData, setFormData] = useState({
    customer_logo1: null,
    customer_joiningDate: "",
    customer_name: "",
    location: "",
    account_manager_mail: "",
    web_address: "",
    address: "",
    billing_address: "",
    contact_person: "",
    cp_position: "",
    cp_contact_num: "",
    cp_mail: "",
    customer_email: "",
    customer_phone: "",
    customer_city: "",
    customer_state: "",
    customer_pincode: ""
  });

  const showNotificationMessage = (type, message) => {
    setNotificationType(type);
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const copyAddressToBilling = (e) => {
    if (e.target.checked) {
      setFormData((prev) => ({ ...prev, billing_address: prev.address }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.customer_name?.trim()) {
      errors.customer_name = "Company name is required";
    }
    if (formData.customer_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customer_email)) {
      errors.customer_email = "Enter a valid email address";
    }
    if (formData.customer_phone && !/^\d{10}$/.test(formData.customer_phone)) {
      errors.customer_phone = "Phone number must be 10 digits";
    }
    if (formData.customer_pincode && !/^\d{6}$/.test(formData.customer_pincode)) {
      errors.customer_pincode = "Pincode must be 6 digits";
    }
    if (formData.account_manager_mail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.account_manager_mail)) {
      errors.account_manager_mail = "Enter a valid email address";
    }
    if (formData.cp_contact_num && !/^\d{10}$/.test(formData.cp_contact_num)) {
      errors.cp_contact_num = "Phone number must be 10 digits";
    }
    if (formData.cp_mail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.cp_mail)) {
      errors.cp_mail = "Enter a valid email address";
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      showNotificationMessage("error", "Please fix the validation errors");
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== "") {
          formDataToSend.append(key, formData[key]);
        }
      });

      await clientService.createClient(formDataToSend);
      showNotificationMessage("success", "Client created successfully!");

      // Reset form
      setFormData({
        customer_logo1: null,
        customer_joiningDate: "",
        customer_name: "",
        location: "",
        account_manager_mail: "",
        web_address: "",
        address: "",
        billing_address: "",
        contact_person: "",
        cp_position: "",
        cp_contact_num: "",
        cp_mail: "",
        customer_email: "",
        customer_phone: "",
        customer_city: "",
        customer_state: "",
        customer_pincode: ""
      });
      formRef.current.reset();

      setTimeout(() => navigate("/client"), 2000);
    } catch (error) {
      console.error("Error creating client:", error);
      let errorMsg = "Failed to create client";
      if (error.response) {
        if (error.response.status === 400) {
          const errorData = error.response.data;
          if (errorData.errors) {
            if (Array.isArray(errorData.errors)) {
              const fieldErrors = {};
              errorData.errors.forEach((err) => {
                fieldErrors[err.field] = err.message;
              });
              setValidationErrors(fieldErrors);
            } else {
              setValidationErrors(errorData.errors);
            }
            showNotificationMessage("error", "Validation failed. Please check the form.");
            setIsSubmitting(false);
            return;
          } else {
            errorMsg = errorData.message || "Validation error";
          }
        } else if (error.response.status === 401) {
          errorMsg = "You are not authorized. Please login again.";
        } else if (error.response.status === 403) {
          errorMsg = "You don't have permission to create clients.";
        } else {
          errorMsg = error.response.data?.message || errorMsg;
        }
      } else if (error.request) {
        errorMsg = "No response from server. Please check your internet connection.";
      }
      showNotificationMessage("error", errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Notification Popup */}
      {showNotification && (
        <div
          className={`notification-popup ${notificationType === "success" ? "notification-success" : "notification-error"}`}
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 9999,
            padding: "12px 16px",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            minWidth: "280px",
            animation: "slideIn 0.3s ease",
            fontSize: "0.9rem"
          }}
        >
          <i className={`bi ${notificationType === "success" ? "bi-check-circle-fill" : "bi-exclamation-triangle-fill"}`}></i>
          <span style={{ flex: 1 }}>{notificationMessage}</span>
          <button
            onClick={() => setShowNotification(false)}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.1rem",
              cursor: "pointer",
              padding: "0 3px"
            }}
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
      )}

      {/* Header with back button */}
      <div className="d-flex align-items-center" style={{ marginBottom: "1rem", marginTop: "0.25rem" }}>
        <button
          onClick={() => navigate("/client")}
          className="btn btn-link text-dark p-0 me-2"
          style={{ fontSize: "1.8rem", lineHeight: 1, textDecoration: "none" }}
          title="Go back to clients list"
        >
          <i className="bi bi-arrow-left"></i>
        </button>
        <h2 style={{ margin: 0, fontSize: "1.8rem", fontWeight: 500, color: "var(--secondary)" }}>Create Client</h2>
      </div>

      <section className="section" style={{ padding: 0 }}>
        <div className="row" style={{ margin: 0 }}>
          <div className="col-lg-12" style={{ padding: 0 }}>
            <div className="card">
              <div className="card-body">
                <form id="clientForm" ref={formRef} noValidate encType="multipart/form-data" onSubmit={handleSubmit}>
                  <div className="row g-3">
                    {/* Company Logo */}
                    <div className="col-md-3">
                      <label htmlFor="inputPhoto" className="form-label">Upload Logo</label>
                      <input
                        name="customer_logo1"
                        type="file"
                        className={`form-control form-control-sm ${validationErrors.customer_logo1 ? "is-invalid" : ""}`}
                        id="inputPhoto"
                        accept="image/*"
                        onChange={handleInputChange}
                      />
                      {validationErrors.customer_logo1 && (
                        <div className="invalid-feedback">{validationErrors.customer_logo1}</div>
                      )}
                    </div>

                    {/* Joining Date */}
                    <div className="col-md-3">
                      <label htmlFor="inputcustJoiningDate" className="form-label">Joining Date</label>
                      <input
                        type="date"
                        name="customer_joiningDate"
                        className={`form-control form-control-sm ${validationErrors.customer_joiningDate ? "is-invalid" : ""}`}
                        id="inputcustJoiningDate"
                        value={formData.customer_joiningDate}
                        onChange={handleInputChange}
                      />
                      {validationErrors.customer_joiningDate && (
                        <div className="invalid-feedback">{validationErrors.customer_joiningDate}</div>
                      )}
                    </div>

                    {/* Company Name (required) */}
                    <div className="col-md-3">
                      <label htmlFor="inputCompanyname" className="form-label">
                        Company Name <span style={{ color: "#dc3545" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="customer_name"
                        className={`form-control form-control-sm ${validationErrors.customer_name ? "is-invalid" : ""}`}
                        id="inputCompanyname"
                        value={formData.customer_name}
                        onChange={handleInputChange}
                        required
                      />
                      {validationErrors.customer_name && (
                        <div className="invalid-feedback">{validationErrors.customer_name}</div>
                      )}
                    </div>

                    {/* Customer Email */}
                    <div className="col-md-3">
                      <label htmlFor="inputCustomerEmail" className="form-label">Customer Email</label>
                      <input
                        type="email"
                        name="customer_email"
                        className={`form-control form-control-sm ${validationErrors.customer_email ? "is-invalid" : ""}`}
                        id="inputCustomerEmail"
                        placeholder="Enter email"
                        value={formData.customer_email}
                        onChange={handleInputChange}
                      />
                      {validationErrors.customer_email && (
                        <div className="invalid-feedback">{validationErrors.customer_email}</div>
                      )}
                    </div>

                    {/* Customer Phone */}
                    <div className="col-md-3">
                      <label htmlFor="inputCustomerPhone" className="form-label">Customer Phone</label>
                      <input
                        type="tel"
                        name="customer_phone"
                        className={`form-control form-control-sm ${validationErrors.customer_phone ? "is-invalid" : ""}`}
                        id="inputCustomerPhone"
                        placeholder="10-digit number"
                        value={formData.customer_phone}
                        onChange={handleInputChange}
                        pattern="[0-9]{10}"
                        maxLength="10"
                      />
                      {validationErrors.customer_phone && (
                        <div className="invalid-feedback">{validationErrors.customer_phone}</div>
                      )}
                    </div>

                    {/* Location */}
                    <div className="col-md-3">
                      <label htmlFor="inputLocation" className="form-label">Location</label>
                      <input
                        type="text"
                        name="location"
                        className={`form-control form-control-sm ${validationErrors.location ? "is-invalid" : ""}`}
                        id="inputLocation"
                        value={formData.location}
                        onChange={handleInputChange}
                      />
                      {validationErrors.location && (
                        <div className="invalid-feedback">{validationErrors.location}</div>
                      )}
                    </div>

                    {/* Account Manager Email */}
                    <div className="col-md-3">
                      <label htmlFor="inputAccountEmail" className="form-label">Account Manager Email</label>
                      <input
                        type="email"
                        name="account_manager_mail"
                        className={`form-control form-control-sm ${validationErrors.account_manager_mail ? "is-invalid" : ""}`}
                        id="inputAccountEmail"
                        value={formData.account_manager_mail}
                        onChange={handleInputChange}
                      />
                      {validationErrors.account_manager_mail && (
                        <div className="invalid-feedback">{validationErrors.account_manager_mail}</div>
                      )}
                    </div>

                    {/* Web Address */}
                    <div className="col-md-3">
                      <label htmlFor="inputWebaddress" className="form-label">Web Address</label>
                      <input
                        type="url"
                        name="web_address"
                        className={`form-control form-control-sm ${validationErrors.web_address ? "is-invalid" : ""}`}
                        id="inputWebaddress"
                        value={formData.web_address}
                        onChange={handleInputChange}
                      />
                      {validationErrors.web_address && (
                        <div className="invalid-feedback">{validationErrors.web_address}</div>
                      )}
                    </div>

                    {/* Address (full width) */}
                    <div className="col-md-6">
                      <label htmlFor="inputAddress" className="form-label">Address</label>
                      <input
                        type="text"
                        name="address"
                        className={`form-control form-control-sm ${validationErrors.address ? "is-invalid" : ""}`}
                        id="inputAddress"
                        placeholder="Street address"
                        value={formData.address}
                        onChange={handleInputChange}
                      />
                      {validationErrors.address && (
                        <div className="invalid-feedback">{validationErrors.address}</div>
                      )}
                    </div>

                    {/* City */}
                    <div className="col-md-2">
                      <label htmlFor="inputCustomerCity" className="form-label">City</label>
                      <input
                        type="text"
                        name="customer_city"
                        className={`form-control form-control-sm ${validationErrors.customer_city ? "is-invalid" : ""}`}
                        id="inputCustomerCity"
                        placeholder="City"
                        value={formData.customer_city}
                        onChange={handleInputChange}
                      />
                      {validationErrors.customer_city && (
                        <div className="invalid-feedback">{validationErrors.customer_city}</div>
                      )}
                    </div>

                    {/* State */}
                    <div className="col-md-2">
                      <label htmlFor="inputCustomerState" className="form-label">State</label>
                      <input
                        type="text"
                        name="customer_state"
                        className={`form-control form-control-sm ${validationErrors.customer_state ? "is-invalid" : ""}`}
                        id="inputCustomerState"
                        placeholder="State"
                        value={formData.customer_state}
                        onChange={handleInputChange}
                      />
                      {validationErrors.customer_state && (
                        <div className="invalid-feedback">{validationErrors.customer_state}</div>
                      )}
                    </div>

                    {/* Pincode */}
                    <div className="col-md-2">
                      <label htmlFor="inputCustomerPincode" className="form-label">Pincode</label>
                      <input
                        type="text"
                        name="customer_pincode"
                        className={`form-control form-control-sm ${validationErrors.customer_pincode ? "is-invalid" : ""}`}
                        id="inputCustomerPincode"
                        placeholder="6-digit"
                        value={formData.customer_pincode}
                        onChange={handleInputChange}
                        pattern="[0-9]{6}"
                        maxLength="6"
                      />
                      {validationErrors.customer_pincode && (
                        <div className="invalid-feedback">{validationErrors.customer_pincode}</div>
                      )}
                    </div>

                    {/* Billing Address */}
                    <div className="col-md-6">
                      <label htmlFor="inputBillingaddress" className="form-label">Billing Address</label>
                      <input
                        type="text"
                        name="billing_address"
                        className={`form-control form-control-sm ${validationErrors.billing_address ? "is-invalid" : ""}`}
                        id="inputBillingaddress"
                        placeholder="Billing address"
                        value={formData.billing_address}
                        onChange={handleInputChange}
                      />
                      {validationErrors.billing_address && (
                        <div className="invalid-feedback">{validationErrors.billing_address}</div>
                      )}
                    </div>

                    {/* Copy address checkbox */}
                    <div className="col-md-12">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="copyAddress"
                          onChange={copyAddressToBilling}
                        />
                        <label className="form-check-label" htmlFor="copyAddress">
                          Copy Address to Billing Address
                        </label>
                      </div>
                    </div>

                    {/* Section: Contact Details */}
                    <div className="col-12">
                      <h6>Contact Details</h6>
                    </div>

                    <div className="col-md-3">
                      <label htmlFor="inputContactperson" className="form-label">Contact Person</label>
                      <input
                        type="text"
                        name="contact_person"
                        className={`form-control form-control-sm ${validationErrors.contact_person ? "is-invalid" : ""}`}
                        id="inputContactperson"
                        placeholder="Contact person"
                        value={formData.contact_person}
                        onChange={handleInputChange}
                      />
                      {validationErrors.contact_person && (
                        <div className="invalid-feedback">{validationErrors.contact_person}</div>
                      )}
                    </div>

                    <div className="col-md-3">
                      <label htmlFor="inputPosition" className="form-label">Position</label>
                      <input
                        type="text"
                        name="cp_position"
                        className={`form-control form-control-sm ${validationErrors.cp_position ? "is-invalid" : ""}`}
                        id="inputPosition"
                        placeholder="Job position"
                        value={formData.cp_position}
                        onChange={handleInputChange}
                        maxLength="100"
                      />
                      {validationErrors.cp_position && (
                        <div className="invalid-feedback">{validationErrors.cp_position}</div>
                      )}
                    </div>

                    <div className="col-md-3">
                      <label htmlFor="inputPhoneNumber" className="form-label">Contact Phone</label>
                      <input
                        type="tel"
                        name="cp_contact_num"
                        className={`form-control form-control-sm ${validationErrors.cp_contact_num ? "is-invalid" : ""}`}
                        id="inputPhoneNumber"
                        placeholder="10-digit number"
                        value={formData.cp_contact_num}
                        onChange={handleInputChange}
                        pattern="[0-9]{10}"
                        maxLength="10"
                      />
                      {validationErrors.cp_contact_num && (
                        <div className="invalid-feedback">{validationErrors.cp_contact_num}</div>
                      )}
                    </div>

                    <div className="col-md-3">
                      <label htmlFor="inputClientEmail" className="form-label">Contact Email</label>
                      <input
                        type="email"
                        name="cp_mail"
                        className={`form-control form-control-sm ${validationErrors.cp_mail ? "is-invalid" : ""}`}
                        id="inputClientEmail"
                        placeholder="Email"
                        value={formData.cp_mail}
                        onChange={handleInputChange}
                      />
                      {validationErrors.cp_mail && (
                        <div className="invalid-feedback">{validationErrors.cp_mail}</div>
                      )}
                    </div>

                    {/* Form actions */}
                    <div className="col-12 d-flex justify-content-end gap-2 mt-3">
                      <button type="button" className="btn btn-secondary" onClick={() => navigate("/client")}>
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-success" disabled={isSubmitting}>
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Creating...
                          </>
                        ) : (
                          "Create Client"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CreateClient;